/**
 * LLMCostControlPage
 * Admin-only dashboard that shows current LLM burn, monthly budget,
 * per-feature kill switches and a panic "kill all non-critical" button.
 *
 * Reads: GET /api/llm-cost/dashboard
 * Writes: PUT /api/llm-cost/config, POST /api/llm-cost/kill-switch,
 *         POST /api/llm-cost/kill-all-non-critical
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Navbar } from "../components/Navbar";
import {
  DollarSign, AlertTriangle, Zap, ShieldAlert, RefreshCw, TrendingUp,
  Activity, AlarmClock, Bell, Send,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRITICALITY_BADGE = {
  core: { label: "Core", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  standard: { label: "Standard", className: "bg-amber-100 text-amber-800 border-amber-200" },
  internal: { label: "Internal", className: "bg-slate-100 text-slate-700 border-slate-200" },
};

export default function LLMCostControlPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingBudget, setSavingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState("");
  const [dailyBudgetInput, setDailyBudgetInput] = useState("");
  const [alertInput, setAlertInput] = useState("");
  const [pushoverToken, setPushoverToken] = useState("");
  const [pushoverKey, setPushoverKey] = useState("");
  const [savingPushover, setSavingPushover] = useState(false);
  const [testingPushover, setTestingPushover] = useState(false);
  const token = localStorage.getItem("token");

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/llm-cost/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setBudgetInput(String(res.data.config.monthly_budget_usd));
      setDailyBudgetInput(String(res.data.config.daily_budget_usd));
      setAlertInput(String(res.data.config.alert_at_percent));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // Refresh every 60s
    const id = setInterval(fetchDashboard, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  const saveBudget = async () => {
    setSavingBudget(true);
    try {
      const newBudget = parseFloat(budgetInput);
      const newDaily = parseFloat(dailyBudgetInput);
      const newAlert = parseInt(alertInput, 10);
      await axios.put(
        `${API_URL}/llm-cost/config`,
        { monthly_budget_usd: newBudget, daily_budget_usd: newDaily, alert_at_percent: newAlert },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Budget: $${newBudget.toFixed(2)}/mo · $${newDaily.toFixed(2)}/day · alert at ${newAlert}%`);
      fetchDashboard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSavingBudget(false);
    }
  };

  const savePushover = async () => {
    setSavingPushover(true);
    try {
      const res = await axios.put(
        `${API_URL}/llm-cost/pushover`,
        { app_token: pushoverToken, user_key: pushoverKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.validation?.ok) {
        toast.success(`Pushover saved & validated (${res.data.validation.devices?.length || 0} device${res.data.validation.devices?.length === 1 ? "" : "s"})`);
      } else if (res.data.configured) {
        toast.success("Pushover credentials saved (validation skipped)");
      } else {
        toast.warning("Pushover credentials cleared");
      }
      setPushoverToken("");
      setPushoverKey("");
      fetchDashboard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save Pushover");
    } finally {
      setSavingPushover(false);
    }
  };

  const sendTestPushover = async () => {
    setTestingPushover(true);
    try {
      await axios.post(
        `${API_URL}/llm-cost/pushover/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Test notification sent — check your phone");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTestingPushover(false);
    }
  };

  const toggleNotification = async (key, value) => {
    try {
      await axios.put(
        `${API_URL}/llm-cost/pushover`,
        { [key]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${key}: ${value ? "ON" : "OFF"}`);
      fetchDashboard();
    } catch (e) {
      toast.error("Failed to toggle notification");
    }
  };

  const toggleAutoStop = async (enabled) => {
    try {
      await axios.put(
        `${API_URL}/llm-cost/config`,
        { auto_stop_at_budget: enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(enabled ? "Auto-stop on budget overrun: ON" : "Auto-stop on budget overrun: OFF");
      fetchDashboard();
    } catch (e) {
      toast.error("Failed to toggle auto-stop");
    }
  };

  const toggleFeature = async (feature, enabled) => {
    try {
      await axios.post(
        `${API_URL}/llm-cost/kill-switch`,
        { feature, enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${feature}: ${enabled ? "ON" : "OFF"}`);
      fetchDashboard();
    } catch (e) {
      toast.error("Failed to toggle feature");
    }
  };

  const killAllNonCritical = async () => {
    if (!window.confirm("PANIC BUTTON\n\nThis will disable EVERY non-core LLM feature (internal + standard) until you turn each one back on individually. Customer-facing core features (WhatsApp assistant, AI Receptionist, Email parser, Sales Quote parser) will stay alive.\n\nProceed?")) return;
    try {
      await axios.post(
        `${API_URL}/llm-cost/kill-all-non-critical`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("All non-critical LLM features disabled");
      fetchDashboard();
    } catch (e) {
      toast.error("Failed to disable non-critical features");
    }
  };

  if (loading || !data) {
    return (
      <>
        <Navbar />
        <div className="p-8 text-slate-600">
          <RefreshCw className="h-5 w-5 animate-spin inline mr-2" />
          Loading LLM cost dashboard…
        </div>
      </>
    );
  }

  const { spend, config, features } = data;
  const pctBarColor =
    spend.month_pct_used >= 100 ? "bg-red-600"
    : spend.month_pct_used >= config.alert_at_percent ? "bg-amber-500"
    : "bg-emerald-500";

  return (
    <>
      <Navbar />
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="llm-cost-control-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-emerald-600" /> LLM Cost Control
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Monthly budget cap + per-feature kill switches. Universal Key billing is authoritative.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchDashboard} data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* HARD KILL — prominent panic strip at top */}
      <Card className="border-red-300 bg-gradient-to-r from-red-50 to-orange-50">
        <CardContent className="pt-6 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-red-900 flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-red-700" />
              HARD KILL — stop all non-core LLM features now
            </h3>
            <p className="text-xs text-slate-600 mt-1">
              Disables every Standard + Internal feature in one click. Core customer-facing features stay alive.
              Re-enable individually below when ready.
            </p>
          </div>
          <Button
            size="lg"
            onClick={killAllNonCritical}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold shadow-md"
            data-testid="kill-non-critical-btn"
          >
            <ShieldAlert className="h-4 w-4 mr-2" />
            KILL NOW
          </Button>
        </CardContent>
      </Card>

      {/* Spend snapshot */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={spend.over_budget ? "border-red-300 bg-red-50" : spend.alert_triggered ? "border-amber-300 bg-amber-50" : ""}>
          <CardContent className="pt-6">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Month-to-date</div>
            <div className="text-3xl font-bold mt-1" data-testid="mtd-spend">${spend.month_to_date_usd.toFixed(2)}</div>
            <div className="text-xs text-slate-500 mt-1">of ${spend.month_budget_usd.toFixed(2)} budget</div>
            <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${pctBarColor} transition-all`}
                style={{ width: `${Math.min(100, spend.month_pct_used)}%` }}
              />
            </div>
            <div className="text-xs mt-1 font-medium">
              {spend.month_pct_used}% used
              {spend.over_budget && <span className="ml-2 text-red-600">⚠ OVER BUDGET</span>}
              {!spend.over_budget && spend.alert_triggered && <span className="ml-2 text-amber-600">⚠ Alert threshold</span>}
            </div>
          </CardContent>
        </Card>

        <Card className={spend.over_daily ? "border-red-300 bg-red-50" : ""}>
          <CardContent className="pt-6">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Today</div>
            <div className="text-3xl font-bold mt-1" data-testid="today-spend">${spend.today_usd.toFixed(2)}</div>
            <div className="text-xs text-slate-500 mt-1">of ${spend.daily_budget_usd.toFixed(2)} daily cap</div>
            <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${spend.over_daily ? "bg-red-600" : spend.daily_pct_used >= 80 ? "bg-amber-500" : "bg-emerald-500"} transition-all`}
                style={{ width: `${Math.min(100, spend.daily_pct_used)}%` }}
              />
            </div>
            <div className="text-xs mt-1 font-medium">
              {spend.daily_pct_used}% used
              {spend.over_daily && <span className="ml-2 text-red-600">⚠ OVER DAILY CAP</span>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Last 7 days</div>
            <div className="text-3xl font-bold mt-1">${spend.week_usd.toFixed(2)}</div>
            <div className="text-xs text-slate-500 mt-1">{spend.total_calls_mtd} calls MTD</div>
          </CardContent>
        </Card>

        <Card className={config.auto_stop_at_budget ? "border-blue-300 bg-blue-50" : ""}>
          <CardContent className="pt-6">
            <div className="text-xs text-slate-500 uppercase tracking-wide flex items-center justify-between">
              Auto-stop at budget
              <Switch
                checked={config.auto_stop_at_budget}
                onCheckedChange={toggleAutoStop}
                data-testid="auto-stop-switch"
              />
            </div>
            <div className="text-sm mt-3 text-slate-600">
              {config.auto_stop_at_budget
                ? "When monthly spend hits 100% of budget, all non-core features auto-disable."
                : "Off — features keep running even past budget. Toggle on for a hard cap."}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Budget config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlarmClock className="h-4 w-4 text-slate-500" />
            Budget settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">Monthly budget (USD)</Label>
              <Input
                type="number"
                step="1"
                min="1"
                max="10000"
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                className="font-mono mt-1"
                data-testid="budget-input"
              />
            </div>
            <div>
              <Label className="text-xs">Daily budget (USD)</Label>
              <Input
                type="number"
                step="0.5"
                min="0.5"
                max="1000"
                value={dailyBudgetInput}
                onChange={(e) => setDailyBudgetInput(e.target.value)}
                className="font-mono mt-1"
                data-testid="daily-budget-input"
              />
            </div>
            <div>
              <Label className="text-xs">Alert at % of budget</Label>
              <Input
                type="number"
                step="5"
                min="10"
                max="100"
                value={alertInput}
                onChange={(e) => setAlertInput(e.target.value)}
                className="font-mono mt-1"
                data-testid="alert-pct-input"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={saveBudget}
                disabled={savingBudget}
                className="w-full"
                data-testid="save-budget-btn"
              >
                {savingBudget ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
          {config.updated_at && (
            <p className="text-xs text-slate-500 mt-3">
              Last updated {new Date(config.updated_at).toLocaleString()} by {config.updated_by || "—"}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Pushover notifications */}
      <Card className={config.pushover_configured ? "border-emerald-200" : "border-amber-200 bg-amber-50/40"}>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-blue-600" />
              Pushover notifications
              {config.pushover_configured ? (
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border bg-emerald-100 text-emerald-800 border-emerald-200">
                  Connected
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border bg-amber-100 text-amber-800 border-amber-200">
                  Not configured
                </span>
              )}
            </span>
            {config.pushover_configured && (
              <Button
                size="sm"
                variant="outline"
                onClick={sendTestPushover}
                disabled={testingPushover}
                data-testid="pushover-test-btn"
              >
                <Send className="h-3.5 w-3.5 mr-1.5" />
                {testingPushover ? "Sending…" : "Send test"}
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Pushover App Token</Label>
              <Input
                type="password"
                value={pushoverToken}
                onChange={(e) => setPushoverToken(e.target.value)}
                placeholder={config.pushover_configured ? "(set — leave blank to keep)" : "30-char application token"}
                className="font-mono mt-1"
                data-testid="pushover-token-input"
              />
              <p className="text-[10px] text-slate-500 mt-1">From pushover.net → Your Applications</p>
            </div>
            <div>
              <Label className="text-xs">Pushover User Key</Label>
              <Input
                type="password"
                value={pushoverKey}
                onChange={(e) => setPushoverKey(e.target.value)}
                placeholder={config.pushover_configured ? "(set — leave blank to keep)" : "30-char user key"}
                className="font-mono mt-1"
                data-testid="pushover-userkey-input"
              />
              <p className="text-[10px] text-slate-500 mt-1">From pushover.net dashboard (top-right)</p>
            </div>
            <div className="flex items-end">
              <Button
                onClick={savePushover}
                disabled={savingPushover || (!pushoverToken && !pushoverKey)}
                className="w-full"
                data-testid="save-pushover-btn"
              >
                {savingPushover ? "Saving…" : "Save & Validate"}
              </Button>
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="text-xs font-medium text-slate-700 mb-2">Which notifications do you want?</div>
            <div className="space-y-2">
              {[
                { key: "daily_summary",   label: "📊 End-of-day usage summary",            time: "Fires at 20:00 Europe/Ljubljana (CET/CEST)" },
                { key: "monthly_summary", label: "📅 End-of-month usage summary",          time: "Fires on the 1st at 00:00 UTC" },
                { key: "burn_alert",      label: "🔥 Burn-gone-wild alert (high priority)", time: "Fires when daily or monthly budget breached, or today's spend ≥ 2× daily cap" },
              ].map((n) => {
                const v = config.notifications?.[n.key] !== false;
                return (
                  <div key={n.key} className="flex items-center justify-between p-2 rounded border border-slate-200 bg-white">
                    <div>
                      <div className="text-sm font-medium">{n.label}</div>
                      <div className="text-[11px] text-slate-500">{n.time}</div>
                    </div>
                    <Switch
                      checked={v}
                      onCheckedChange={(val) => toggleNotification(n.key, val)}
                      data-testid={`notif-${n.key}-switch`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Kill switches */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-500" />
            Feature kill switches
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {features.map((f) => {
              const enabled = config.kill_switches[f.key] !== false;
              const badge = CRITICALITY_BADGE[f.criticality] || CRITICALITY_BADGE.standard;
              return (
                <div
                  key={f.key}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    enabled ? "border-slate-200 bg-white" : "border-red-200 bg-red-50"
                  }`}
                  data-testid={`feature-row-${f.key}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{f.label}</span>
                      <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${badge.className}`}>
                        {badge.label}
                      </span>
                    </div>
                    <code className="text-[11px] text-slate-500">{f.key}</code>
                  </div>
                  <Switch
                    checked={enabled}
                    onCheckedChange={(v) => toggleFeature(f.key, v)}
                    data-testid={`feature-switch-${f.key}`}
                  />
                </div>
              );
            })}
          </div>
          <div className="mt-4 text-xs text-slate-500 leading-relaxed">
            <strong>Core</strong> = customer-facing features (kept alive by Panic button).
            <br />
            <strong>Standard</strong> = nice-to-have, safe to disable temporarily.
            <br />
            <strong>Internal</strong> = staff-only, first to disable when budget tightens.
            <br />
            <em>Note:</em> Call sites must check{" "}
            <code className="bg-slate-100 px-1 rounded">is_llm_feature_enabled()</code> for switches
            to actually gate spending. Switches that are off but unchecked at call-site still spend
            (graceful adoption — wire each call site as we adopt the helper).
          </div>
        </CardContent>
      </Card>

      {/* Top burners */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-slate-500" /> Top sites this month
            </CardTitle>
          </CardHeader>
          <CardContent>
            {spend.by_site_mtd.length === 0 ? (
              <p className="text-sm text-slate-500 italic">
                No tracked LLM calls yet this month. Only sites that call <code>audit_llm_usage()</code> appear here.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 border-b">
                    <tr>
                      <th className="text-left py-2 px-2">Site</th>
                      <th className="text-left py-2 px-2">Model</th>
                      <th className="text-right py-2 px-2">Calls</th>
                      <th className="text-right py-2 px-2">Cost ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spend.by_site_mtd.slice(0, 12).map((row, i) => (
                      <tr key={i} className="border-b hover:bg-slate-50">
                        <td className="py-2 px-2 font-mono text-xs">{row.site}</td>
                        <td className="py-2 px-2 text-xs">{row.model}</td>
                        <td className="py-2 px-2 text-right font-mono text-xs">{row.calls}</td>
                        <td className="py-2 px-2 text-right font-mono font-semibold">{row.cost_usd.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4 text-slate-500" /> By model this month
            </CardTitle>
          </CardHeader>
          <CardContent>
            {spend.by_model_mtd.length === 0 ? (
              <p className="text-sm text-slate-500 italic">No tracked calls this month.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 border-b">
                    <tr>
                      <th className="text-left py-2 px-2">Model</th>
                      <th className="text-right py-2 px-2">Calls</th>
                      <th className="text-right py-2 px-2">Cost ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spend.by_model_mtd.map((row, i) => (
                      <tr key={i} className="border-b hover:bg-slate-50">
                        <td className="py-2 px-2 font-mono text-xs">{row.model}</td>
                        <td className="py-2 px-2 text-right font-mono text-xs">{row.calls}</td>
                        <td className="py-2 px-2 text-right font-mono font-semibold">{row.cost_usd.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 30-day daily trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">30-day spend trend</CardTitle>
        </CardHeader>
        <CardContent>
          {spend.by_day_30d.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No spend data yet.</p>
          ) : (
            <div className="flex items-end gap-1 h-32">
              {spend.by_day_30d.map((d, i) => {
                const max = Math.max(...spend.by_day_30d.map((x) => x.cost_usd), 0.0001);
                const h = Math.max(4, (d.cost_usd / max) * 120);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center group" title={`${d.day}: $${d.cost_usd.toFixed(4)} (${d.calls} calls)`}>
                    <div
                      className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-t transition-colors"
                      style={{ height: `${h}px` }}
                    />
                    <div className="text-[9px] text-slate-400 mt-1 rotate-45 origin-top-left whitespace-nowrap">
                      {d.day.slice(5)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer notes */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-6 text-xs text-slate-600 space-y-1.5">
          <div className="flex gap-2"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
            <span>Only call sites that invoke <code className="bg-white px-1 rounded">audit_llm_usage()</code> are tracked. Many sites aren't yet wired — real spend may exceed what's shown.</span>
          </div>
          <div className="flex gap-2"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
            <span>The Universal Key billing page on Emergent (Profile → Universal Key) is the authoritative source of spend.</span>
          </div>
          <div className="flex gap-2"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
            <span>Kill switches only have effect at call sites that check <code className="bg-white px-1 rounded">is_llm_feature_enabled()</code>. New sites adopting the helper benefit immediately; legacy sites keep spending until wired.</span>
          </div>
        </CardContent>
      </Card>
      </div>
    </>
  );
}
